import { mm } from "@inovitas/infra3dsdk";
import { NavigateBefore, NavigateNext, Upgrade } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Feature } from "geojson";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { UseActiveLayer } from "shared/common/hooks/UseActiveLayer";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { manageKeyboardEvents } from "shared/common/utils/keyboardEvents/manageKeyboardEvents";
import {
  getColumnDefinitionsFromLayer,
  getFeatureCollectionFromLayer,
  getFeaturesCountFromLayer,
  Sorter,
} from "shared/common/utils/layers/layerutils";
import FeatureCell from "./FeatureCell";

export interface FeatureColumn {
  id: number;
  key: string;
  name: string;
  datatype: number;
  editable: boolean;
  values?: string[];
}

export default function FeatureListTable({
  sorter,
  setSorter,
  pageNumber,
  setPageNumber,
  numberPerPage,
  setNumberPerPage,
}: {
  sorter?: Sorter;
  setSorter: (sorter: Sorter | undefined) => void;
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  numberPerPage: number;
  setNumberPerPage: (numberPerPage: number) => void;
}) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeDeleteFeatureWithID = defaultStore(
    (state) => state.changeDeleteFeatureWithID
  );
  const changeAlertDeleteFeature = defaultStore(
    (state) => state.changeAlertDeleteFeature
  );

  const changeSelectedTableRows = sidebarStore(
    (state) => state.changeSelectedTableRows
  );
  const changeEditFeatureTableRow = sidebarStore(
    (state) => state.changeEditFeatureTableRow
  );
  const enableEditFeature = sidebarStore((state) => state.enableEditFeature);
  const enableDeleteFeature = sidebarStore(
    (state) => state.enableDeleteFeature
  );
  const updateFeaturesList = sidebarStore((state) => state.updateFeaturesList);
  const changeUpdateFeaturesList = sidebarStore(
    (state) => state.changeUpdateFeaturesList
  );

  const activeLayer = UseActiveLayer(viewer);

  const [featureTableColumns, setFeatureTableColumns] = useState<
    FeatureColumn[]
  >([]);
  const [featuresCount, setFeaturesCount] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>();
  const [selectedFilterColumn, setSelectedFilterColumn] = useState<
    FeatureColumn | undefined
  >(undefined);
  const [pagedFeatures, setPagedFeatures] = useState<Feature[]>([]);

  const handleChangeNumberPerPage = (number: number) => {
    setNumberPerPage(number);
    setPageNumber(1);
  };

  const handleChangeFilterColumn = (name: string) => {
    if (name === "") {
      setSearchInput("");
    }
    const col = featureTableColumns.find((item) => item.key === name);
    setSelectedFilterColumn(col);
  };

  const handleChangePageNumber = (increment: number) => {
    setPageNumber(pageNumber + increment);
  };

  const updateColumns = (layer: mm.Layer) => {
    setFeatureTableColumns(getColumnDefinitionsFromLayer(layer));
  };

  const updateFeaturesCount = (layer: mm.Layer) => {
    getFeaturesCountFromLayer(layer)
      .then((count) => {
        setFeaturesCount(count);
      })
      .catch((error) => {
        // TODO: Inform the user about the error
        console.error(error);
      });
  };

  const updatePagedFeatures = (layer: mm.Layer) => {
    const sorters = sorter ? [sorter] : undefined;
    getFeatureCollectionFromLayer(
      layer,
      numberPerPage * (pageNumber - 1),
      numberPerPage,
      sorters,
      viewer?.getScene().getCurrentGeoframe().srid
    )
      .then((geojson) => {
        setPagedFeatures(geojson.features);
      })
      .catch((error) => {
        // TODO: Inform the user about the error
        console.error(error);
      });
  };

  const handleUpdateFeatureCell = (
    value: string,
    item: Feature,
    columnName: string
  ) => {
    if (!viewer || !item.properties) return;
    item.properties[columnName] = value;

    if (activeLayer !== null && item.id !== null) {
      activeLayer.getLayerEditor().updateFeatures(item);
    }
  };

  const handleSelectFeature = (feature: Feature) => {
    if (enableEditFeature) {
      changeEditFeatureTableRow(feature);
    } else if (enableDeleteFeature) {
      changeAlertDeleteFeature(true);
      changeDeleteFeatureWithID(String(feature.id));
    } else {
      changeSelectedTableRows(feature);
    }
  };

  useEffect(() => {
    if (!viewer) return;
    setFeatureTableColumns([]);
    setFeaturesCount(0);
    setPagedFeatures([]);

    if (activeLayer === null) return;
    updateColumns(activeLayer);
    updateFeaturesCount(activeLayer);
    updatePagedFeatures(activeLayer);
  }, [activeLayer]);

  useEffect(() => {
    if (!viewer) return;
    if (activeLayer !== null) {
      setSorter(undefined);
      if (searchInput && selectedFilterColumn) {
        let operator = "";
        if (
          selectedFilterColumn.datatype === mm.LAYERPROPERTYTYPE.STRING ||
          selectedFilterColumn.datatype === mm.LAYERPROPERTYTYPE.ENUM
        ) {
          operator = "like";
        } else if (
          selectedFilterColumn.datatype === mm.LAYERPROPERTYTYPE.NUMBER ||
          selectedFilterColumn.datatype === mm.LAYERPROPERTYTYPE.INTEGER ||
          selectedFilterColumn.datatype === mm.LAYERPROPERTYTYPE.DATE
        ) {
          operator = "eq";
        }
        activeLayer.filters = [
          {
            property: selectedFilterColumn.key,
            operator: operator,
            value: searchInput,
          },
        ];
      } else {
        activeLayer.filters = [];
      }
      setPageNumber(1);

      // TODO: Add a loading spinner
      updateFeaturesCount(activeLayer);
      updatePagedFeatures(activeLayer);
      activeLayer.reload();
    }
  }, [searchInput, selectedFilterColumn]);

  //when deactivate edit feature than remove from selection selected feature
  useEffect(() => {
    if (!enableEditFeature) {
      changeEditFeatureTableRow(undefined);
    }
  }, [enableEditFeature]);

  useEffect(() => {
    if (viewer !== undefined) {
      if (activeLayer === null) return;
      // TODO: Add a loading spinner
      updatePagedFeatures(activeLayer);
    }
  }, [pageNumber, numberPerPage, sorter]);

  // update listLayers only in parents or outside component places
  useEffect(() => {
    if (!viewer) return;
    if (updateFeaturesList) {
      if (activeLayer === null) return;
      updateFeaturesCount(activeLayer);
      updatePagedFeatures(activeLayer);
    }
  }, [updateFeaturesList]);

  return (
    <Box
      position={"relative"}
      width={"100%"}
      height={"100%"}
      display={"flex"}
      flexDirection={"column"}
      overflow="hidden"
    >
      <Box
        display={
          activeLayer?.type === mm.LAYERTYPE.ARCGISLAYER ||
          activeLayer?.type === mm.LAYERTYPE.INFRA3DLAYER ||
          activeLayer?.type === mm.LAYERTYPE.WFSLAYER
            ? "flex"
            : "none"
        }
        flexDirection="row"
        alignItems={"center"}
        gap={2}
        width="100%"
        position="sticky"
        top={0}
        sx={{
          backgroundColor: darkTheme ? "darkblue.main" : "white",
          borderBottom: darkTheme ? "1px white solid" : "1px #053952 solid",
        }}
      >
        <select
          onChange={(event) => handleChangeFilterColumn(event.target.value)}
          value={
            selectedFilterColumn?.key || activeLayer?.filters[0]?.property || ""
          }
          id="selectFilterColumn"
          style={{
            padding: "2px 0px",
            border: "none",
            outline: "none",
            width: "auto",
            fontWeight: 500,
            color: darkTheme ? "white" : "#053952",
            backgroundColor: darkTheme ? "#053952" : "white",
            fontSize: "12px",
          }}
        >
          <option value="">
            {t("layers_window.feature_table_select_column")}
          </option>
          {featureTableColumns.map((item: FeatureColumn) => (
            <option key={item.key} value={item.key}>
              {item.name}
            </option>
          ))}
        </select>
        <InputBase
          disabled={selectedFilterColumn === undefined}
          classes={{
            disabled: darkTheme
              ? "infra3d-inputTextDisabledDark"
              : "infra3d-inputTextDisabledLight",
          }}
          sx={{
            color: darkTheme ? "white" : "darkblue.main",
            width: "max-content",
            padding: "0px 4px",
            fontSize: "14px",
          }}
          type={
            selectedFilterColumn
              ? [
                  mm.LAYERPROPERTYTYPE.NUMBER,
                  mm.LAYERPROPERTYTYPE.INTEGER,
                ].includes(selectedFilterColumn.datatype)
                ? "number"
                : "text"
              : "text"
          }
          placeholder={
            selectedFilterColumn === undefined
              ? t("layers_window.feature_table_search_placeholder_before") ||
                undefined
              : t("layers_window.feature_table_search_placeholder_after") ||
                undefined
          }
          onFocus={() => manageKeyboardEvents(viewer, true)}
          onBlur={() => manageKeyboardEvents(viewer, false)}
          value={searchInput || activeLayer?.filters[0]?.value || ""}
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
        />
      </Box>
      <Table
        stickyHeader
        sx={{ display: "block", overflow: "auto", height: "100%" }}
      >
        <TableHead>
          <TableRow>
            {featureTableColumns.map((item) => (
              <TableCell
                sx={{
                  width: "1%",
                  padding: "4px",
                  backgroundColor: darkTheme ? "darkblue.main" : "white",
                }}
                key={item.key}
              >
                <Box display={"flex"} alignItems={"center"}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                    color={darkTheme ? "white" : "darkblue.main"}
                  >
                    {item.name}
                  </Typography>
                  <IconButton
                    sx={{
                      display:
                        activeLayer?.type === mm.LAYERTYPE.ARCGISLAYER ||
                        activeLayer?.type === mm.LAYERTYPE.MEMORYLAYER ||
                        activeLayer?.type === mm.LAYERTYPE.INFRA3DLAYER ||
                        activeLayer?.type === mm.LAYERTYPE.WFSLAYER ||
                        activeLayer?.type === mm.LAYERTYPE.WFSQGISLAYER
                          ? ""
                          : "none",
                      "@media (hover: hover)": {
                        ":hover": {
                          color: "secondary.main",
                        },
                      },
                      color:
                        sorter && sorter.property === item.key
                          ? darkTheme
                            ? "secondary.main"
                            : "darkblue.main"
                          : "grey.main",
                      rotate:
                        sorter &&
                        sorter.property === item.key &&
                        sorter.direction !== "ASC"
                          ? "0deg"
                          : "180deg",
                      marginLeft: "8px",
                      transition: "rotate 0.2s",
                      padding: "2px",
                    }}
                    size="small"
                    disableRipple={true}
                    onClick={() => {
                      if (sorter && sorter.property === item.key) {
                        setSorter({
                          property: item.key,
                          direction:
                            sorter.direction === "ASC" ? "DESC" : "ASC",
                        });
                      } else {
                        setSorter({ property: item.key, direction: "ASC" });
                      }
                    }}
                  >
                    <Upgrade />
                  </IconButton>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={{ height: "100%", overflow: "auto" }}>
          {pagedFeatures.length > 0 &&
            pagedFeatures
              .filter((item) => {
                const deleted = activeLayer
                  ?.getLayerEditor()
                  ?.featuresdelete.find(
                    (id: string | number) =>
                      item.id?.toString() === id.toString()
                  );
                return deleted ? false : true;
              })
              .map((item, index: number) => (
                <TableRow
                  sx={{
                    "@media (hover: hover)": {
                      ":hover": {
                        backgroundColor: darkTheme
                          ? "primary.light"
                          : "darkblue.light",
                        cursor: "pointer",
                      },
                    },
                  }}
                  onClick={() => handleSelectFeature(item)}
                  key={index}
                >
                  {featureTableColumns.map(
                    (col: FeatureColumn, indexCol: number) => (
                      <FeatureCell
                        key={indexCol}
                        item={item}
                        col={col}
                        indexCol={indexCol}
                        onChange={handleUpdateFeatureCell}
                      />
                    )
                  )}
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <Box
        width={"100%"}
        position={"sticky"}
        bottom={0}
        sx={{ backgroundColor: darkTheme ? "darkblue.main" : "white" }}
        display="flex"
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={2}
        flexDirection={"row"}
      >
        <Box display={"flex"} alignItems={"end"}>
          <Typography
            color={darkTheme ? "white" : "darkblue.main"}
            fontWeight={700}
            fontSize="12px"
          >
            {t("layers_window.feature_table_rows_per_page")}:{" "}
          </Typography>
          <select
            onChange={(event) =>
              handleChangeNumberPerPage(Number(event.target.value))
            }
            defaultValue={numberPerPage}
            style={{
              padding: "2px 4px 4px 4px",
              border: "none",
              outline: "none",
              fontWeight: 700,
              color: darkTheme ? "white" : "#053952",
              backgroundColor: darkTheme ? "#053952" : "transparent",
              fontSize: "12px",
            }}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
          </select>
        </Box>
        <Typography
          color={darkTheme ? "white" : "darkblue.main"}
          fontWeight={700}
          fontSize="12px"
        >
          {1 + numberPerPage * (pageNumber - 1)}~{numberPerPage * pageNumber} of{" "}
          {featuresCount}
        </Typography>
        <Box>
          <IconButton
            disabled={pageNumber == 1}
            onClick={() => handleChangePageNumber(-1)}
            disableRipple={true}
            size="small"
            sx={{
              "@media (hover: hover)": {
                ":hover": {
                  color: "secondary.main",
                },
              },
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            <NavigateBefore />
          </IconButton>
          <IconButton
            disabled={Math.ceil(featuresCount / numberPerPage) <= pageNumber}
            onClick={() => handleChangePageNumber(1)}
            disableRipple={true}
            size="small"
            sx={{
              "@media (hover: hover)": {
                ":hover": {
                  color: "secondary.main",
                },
              },
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            <NavigateNext />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
